import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import env from '../../../environment';
import type { FeaturePageDataInterface } from './types';

export const connectMultipleSources: FeaturePageDataInterface = {
  slug: 'connect-multiple-sources',
  navTitle: 'Crawl multiple hosts & directories',
  shortTitle: 'Multiple hosts',
  navDescription:
    'Discover and crawl hosts across directories and markets — one project collects structured data from many sources.',
  Icon: CloudArrowUpIcon,
  headline: 'Crawl multiple hosts into',
  highlight: 'one research project',
  blurb:
    'Market research rarely lives on a single site. Point Stark Crawl at directories, listings, and competitor hosts — discover pages, extract structured fields, and capture prospects across all of them.',
  quote: {
    text: 'We used to stitch five directory scrapes by hand. Now one project holds every accepted host and the prospects land in one export.',
    author: 'Customer name',
    authorRole: 'Job title, Company',
  },
  image: {
    src: '/images/shots/multi-hosts.png',
    alt: 'Stark Crawl showing pending hosts discovered across multiple directories',
  },
  content: [
    {
      type: 'block',
      body: {
        heading: 'Coverage is scattered; projects should not be',
        body: [
          'Category directories, regional listings, competitor sites, and niche vertical pages rarely sit in one place. Analysts should not have to run a separate scraper for each host.',
          'Stark Crawl discovers and accepts hosts into a single project. Seed a directory, add known domains, and review proposed hosts before they enter the crawl set.',
        ],
        large: true,
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Every host, side by side',
        body: [
          'Pending and accepted hosts stay visible with status, source, and country — so you can see whether coverage still has gaps before you extract.',
          'When extraction runs, fields are filled against the same schema across hosts, so rows stay comparable even when the underlying pages differ.',
        ],
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Stays useful as the market changes',
        body: [
          'Markets grow and directories reshuffle. Add new seed URLs, accept fresh discoveries, and retire hosts that no longer matter — without starting a new codebase.',
          'Need separate markets or segments? Each project has its own host set, schema, and access rules so DACH food, Benelux retail, and a competitor watchlist stay cleanly separated.',
        ],
      },
    },
    {
      type: 'solution',
      body: 'Seed directories and competitor hosts into one project — discovery, acceptance, and extraction stay aligned.',
      cta: 'Try for free',
      url: env.appUrl + '/register',
    },
  ],
  seo: {
    title: 'Crawl multiple hosts and directories - Stark Crawl',
    description:
      'Discover and crawl hosts across directories and markets — one project collects structured prospect data from many sources.',
    keywords:
      'multi-host crawl, directory crawl, market research sources, prospect discovery, Stark Crawl',
  },
  ctaTitle: 'One project, every host that matters',
  ctaBody:
    'Discover hosts across directories and markets, accept what belongs in the study, and extract structured data in one place.',
};
